import React, { useEffect, useState, useRef } from "react";
import api from '../services/api';
import { FaPrint, FaExternalLinkAlt, FaFileExport, FaSpinner } from "react-icons/fa";
import { useLocation } from "react-router-dom";
import { LoadPanel } from "devextreme-react/load-panel";

const DocumentViewer = ({ reportUrl, exportUrl, reportName }) => {
  const location = useLocation();
  const { selectedIds, selectedItems } = location.state || {};
  
  const [showDropdown, setShowDropdown] = useState(false);
  const [iframeSrc, setIframeSrc] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [documentBlobUrl, setDocumentBlobUrl] = useState(null);
  const [error, setError] = useState(null);
  const dropdownRef = useRef(null);
  const containerRef = useRef(null);

  // Detect mobile devices
  useEffect(() => {
    const checkIfMobile = () => {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
        window.innerWidth < 768;
    };
    setIsMobile(checkIfMobile());
  }, []);

  // Fetch document data
  useEffect(() => {
    let isMounted = true;
    const abortController = new AbortController();

    const fetchDocument = async () => {
      if (!selectedIds?.length || !selectedItems?.length) {
        if (isMounted) {
          setError("No valid IDs or document types found");
          setIsLoading(false);
        }
        return;
      }

      try {
        if (isMounted) {
          setIsLoading(true);
          setError(null);
        }
        
        const url = `${reportUrl}?docTypes=${encodeURIComponent(selectedItems.join(","))}`;
        const response = await api.post(url, selectedIds, {
          responseType: 'blob',
          signal: abortController.signal,
          timeout: 990000
        });

        const blob = new Blob([response.data], { 
          type: response.headers['content-type'] || 'application/pdf' 
        });
        const blobUrl = URL.createObjectURL(blob);

        if (isMounted) {
          setDocumentBlobUrl(blobUrl);
          if (isMobile) {
            window.open(blobUrl, '_blank');
          } else {
            setIframeSrc(`${blobUrl}#zoom=100`);
          }
        }

      } catch (error) {
        if (!abortController.signal.aborted && isMounted) {
          setError(error.message || "Failed to load document");
          console.error("Document fetch error:", error);
        }
      } finally {
        if (isMounted) setIsLoading(false);
      }
    };

    const timer = setTimeout(fetchDocument, 50);

    return () => {
      isMounted = false;
      abortController.abort();
      clearTimeout(timer);
      if (documentBlobUrl) URL.revokeObjectURL(documentBlobUrl);
    };
  }, [selectedIds, reportUrl, selectedItems, isMobile]);

  const getFileExtension = (format) => {
    switch(format) {
      case 'pdf': return 'pdf';
      case 'xlsx': return 'xlsx';
      case 'docx': return 'docx';
      case 'html': return 'html';
      case 'image': return 'png';  // Changed to png
      default: return format;
    }
  };

  const handlePrint = () => {
    if (!documentBlobUrl) return;

    if (isMobile) {
      window.open(documentBlobUrl, '_blank');
    } else {
      const printWindow = window.open(documentBlobUrl, "_blank");
      if (printWindow) {
        printWindow.onload = () => printWindow.print();
      }
    }
  };

  const handleExport = async (format) => {    
    if (!exportUrl || !selectedIds?.length || !selectedItems?.length) {
      setError("Missing export parameters");
      return;
    }

    try {
      setIsExporting(true);
      setShowDropdown(false);
      setError(null);

      const url = `${exportUrl}?format=${format}&reportName=${encodeURIComponent(reportName)}&docTypes=${encodeURIComponent(selectedItems.join(","))}`;
      const response = await api.post(url, selectedIds, {
        responseType: 'blob',
        timeout: 990000
      });

      const blob = new Blob([response.data], { 
        type: response.headers['content-type'] || 'application/octet-stream' 
      });
      const downloadUrl = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = downloadUrl;
    
      if(selectedIds?.length<2)
        link.download = `${reportName}_${new Date().toISOString().slice(0,10)}.${getFileExtension(format)}`;
      else
        link.download = `${reportName}_${new Date().toISOString().slice(0,10)}.zip`;

      document.body.appendChild(link);
      link.click();
      
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(downloadUrl);
      }, 100);

    } catch (error) {
      setError(error.message || "Export failed");
      console.error("Export error:", error);
    } finally {
      setIsExporting(false);
    }
  };

  // Mobile view
  if (isMobile) {
    return (
      <div style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        height: "100vh",
        padding: "20px",
        textAlign: "center",
        backgroundColor: "#f5f5f5"
      }}>
        <h3 style={{ fontSize: "20px", marginBottom: "15px", color: "#333" }}>{reportName}</h3>
        
        {error ? (
          <p style={{ color: "red", marginBottom: "20px" }}>{error}</p>
        ) : (
          <p style={{ fontSize: "16px", marginBottom: "30px", color: "#666" }}>
            {documentBlobUrl 
              ? "The document should open in a new tab automatically."
              : "Preparing your document..."}
          </p>
        )}
        
        {documentBlobUrl && (
          <div style={{
            display: "flex",
            flexDirection: "column",
            gap: "15px",
            marginTop: "20px",
            width: "100%",
            maxWidth: "300px"
          }}>
            <button 
              onClick={() => window.open(documentBlobUrl, '_blank')}
              style={mobileButtonStyle}
            >
              <FaExternalLinkAlt style={{ marginRight: "8px" }} />
              Open Document
            </button>
            
            <div style={{ position: "relative" }} ref={dropdownRef}>
              <button
                style={mobileButtonStyle}
                onClick={() => setShowDropdown(!showDropdown)}
                disabled={isExporting}
              >
                {isExporting ? (
                  <FaSpinner className="spin" style={{ marginRight: "8px" }} />
                ) : (
                  <FaFileExport style={{ marginRight: "8px" }} />
                )}
                Export Document
              </button>
              {showDropdown && !isExporting && (
                <div style={mobileDropdownMenuStyle}>
                  {["pdf", "xlsx", "docx", "html", "image"].map(format => (
                    <button
                      key={format}
                      style={mobileDropdownItemStyle}
                      onClick={() => handleExport(format)}
                    >
                      {format === "pdf" && "📄 PDF"}
                      {format === "xlsx" && "📊 Excel"}
                      {format === "docx" && "📜 Word"}
                      {format === "html" && "🌐 Html"}
                      {format === "image" && "🖼️ Image"}
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            <button 
              onClick={handlePrint}
              style={mobileButtonStyle}
            >
              <FaPrint style={{ marginRight: "8px" }} />
              Print Document
            </button>
          </div>
        )}
      </div>
    );
  }

  // Desktop view
  return (
    <div ref={containerRef} style={{
      display: "flex",
      flexDirection: "column",
      height: "100vh",
      width: "100vw",
      backgroundColor: "#fff",
      position: "relative"
    }}>
      <LoadPanel
        visible={isLoading || isExporting}
        showIndicator={true}
        showPane={true}
        shading={true}
        hideOnOutsideClick={false}
        message={isExporting ? "Exporting..." : "Loading document..."}
        position={{ of: containerRef.current }}
        closeOnOutsideClick={false}
        deferRendering={false}
      />

      {error && (
        <div style={{
          padding: "10px",
          backgroundColor: "#ffebee",
          color: "#d32f2f",
          textAlign: "center"
        }}>
          {error}
        </div>
      )}

      <div style={{
        padding: "10px",
        backgroundColor: "#f5f5f5",
        borderBottom: "1px solid #ddd",
        textAlign: "center",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: "15px",
        position: "relative",
        zIndex: 1
      }}>
        <button onClick={handlePrint} style={buttonStyle}>
          <FaPrint size={20} />
        </button>

        <a href={iframeSrc} target="_blank" rel="noopener noreferrer">
          <button style={buttonStyle}>
            <FaExternalLinkAlt size={20} />
          </button>
        </a>

        <div style={{ position: "relative" }} ref={dropdownRef}>
          <button
            style={buttonStyle}
            onClick={() => setShowDropdown(!showDropdown)}
            disabled={isExporting}
          >
            {isExporting ? (
              <FaSpinner className="spin" size={20} />
            ) : (
              <FaFileExport size={20} />
            )}
          </button>
          {showDropdown && !isExporting && (
            <div
              style={dropdownMenuStyle}
              onMouseEnter={() => setShowDropdown(true)}
              onMouseLeave={() => setShowDropdown(false)}
            >
              {["pdf", "xlsx", "docx", "html", "image"].map(format => (
                <button
                  key={format}
                  style={dropdownItemStyle}
                  onClick={() => handleExport(format)}
                >
                  {format === "pdf" && "📄 PDF"}
                  {format === "xlsx" && "📊 XLSX"}
                  {format === "docx" && "📜 DOCX"}
                  {format === "html" && "🌐 HTML"}
                  {format === "image" && "🖼️ Image"}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div style={{ flexGrow: 1, overflow: "hidden" }}>
        {iframeSrc && (
          <iframe
            src={iframeSrc}
            width="100%"
            height="100%"
            style={{
              border: "none",
              backgroundColor: "#fff",
            }}
            scrolling="no"
            seamless
          ></iframe>
        )}
      </div>

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        .spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

// Style constants (same as before)
const buttonStyle = {
  border: "none",
  backgroundColor: "#323232",
  color: "white",
  padding: "10px",
  borderRadius: "5px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "40px",
  height: "40px",
};

const dropdownMenuStyle = {
  position: "absolute",
  top: "45px",
  left: "0",
  backgroundColor: "#fff",
  border: "1px solid #ddd",
  boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
  borderRadius: "5px",
  zIndex: "1000",
  display: "flex",
  flexDirection: "column",
  width: "150px",
};

const dropdownItemStyle = {
  padding: "10px 15px",
  border: "none",
  backgroundColor: "white",
  cursor: "pointer",
  textAlign: "left",
  fontSize: "14px",
  width: "100%",
  display: "flex",
  alignItems: "center",
  gap: "5px",
  transition: "background 0.2s",
};

const mobileButtonStyle = {
  border: "none",
  backgroundColor: "#323232",
  color: "white",
  padding: "12px 15px",
  borderRadius: "5px",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "100%",
  fontSize: "16px",
};

const mobileDropdownMenuStyle = {
  position: "relative",
  backgroundColor: "#fff",
  border: "1px solid #ddd",
  boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
  borderRadius: "5px",
  zIndex: "1000",
  display: "flex",
  flexDirection: "column",
  width: "100%",
  marginTop: "10px",
};

const mobileDropdownItemStyle = {
  padding: "12px 15px",
  border: "none",
  backgroundColor: "white",
  cursor: "pointer",
  textAlign: "left",
  fontSize: "16px",
  width: "100%",
  display: "flex",
  alignItems: "center",
  gap: "10px",
};

export default DocumentViewer;